import { type AriaAttributes, type ReactNode, useId } from 'react'

export type SwitchProps = {
	checked: boolean
	onChange: (checked: boolean) => void
	disabled?: boolean
	className?: string
} & Pick<AriaAttributes, 'aria-label' | 'aria-labelledby' | 'aria-describedby'>

export function Switch({ checked, onChange, disabled = false, className = '', ...aria }: SwitchProps) {
	return (
		<button
			type="button"
			role="switch"
			aria-checked={checked}
			disabled={disabled}
			onClick={() => onChange(!checked)}
			className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-brand-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-60
				${checked ? 'bg-brand-600' : 'bg-grey-300'} ${className}`}
			{...aria}
		>
			<span
				className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform
					${checked ? 'translate-x-6' : 'translate-x-1'}`}
			/>
		</button>
	)
}

export type SwitchFieldProps = {
	checked: boolean
	onChange: (checked: boolean) => void
	label: ReactNode
	description?: ReactNode
	disabled?: boolean
	isLoading?: boolean
	labelAddon?: ReactNode
	children?: ReactNode
}

export function SwitchField(
	{ checked, onChange, label, description, disabled, isLoading = false, labelAddon, children }: SwitchFieldProps,
) {
	const labelId = useId()
	const descriptionId = useId()

	return (
		<div className="flex items-start gap-3 rounded-lg bg-grey-50 p-4">
			{isLoading
				? <div className="mt-0.5 h-6 w-11 flex-shrink-0 rounded-full bg-grey-200 animate-pulse" />
				: (
					<Switch
						checked={checked}
						onChange={onChange}
						disabled={disabled}
						aria-labelledby={labelId}
						aria-describedby={description ? descriptionId : undefined}
						className="mt-0.5"
					/>
				)}
			<div className="min-w-0">
				<div className="flex items-center gap-2">
					<div id={labelId} className="text-sm font-medium text-grey-900">{label}</div>
					{labelAddon}
				</div>
				{description && <p id={descriptionId} className="text-xs text-grey-500 mt-0.5">{description}</p>}
				{children}
			</div>
		</div>
	)
}
